<template>
  <div v-if="isIndianExporter" class="legal-container">
    {{ isDocumentESigned() }}
    <div class="legal-inner-container justify-content-center">
      <div
        class="drip-h1 font-weight-700 color-noble-blue-500 color-noble-blue-500"
      >
        {{ $t('legal.page1.mainTitle') }}
      </div>
      <div class="documents-section">
        <div class="drip-h4 color-noble-blue-500">
          {{ $t('legal.page1.subtitle') }}
        </div>
        <div class="spacing-8"></div>
        <ul class="text-left mb-0">
          <li
            v-for="(doc, i) in document_list"
            :key="i"
            class="drip-body color-noble-blue-500 font-weight-400 mb-1"
          >
            {{ doc }}
          </li>
        </ul>
      </div>
      <div class="spacing-32"></div>
      <div class="spacing-8"></div>
      <div v-if="['primary', 'secondary'].includes($store.getters.user.ctype)">
        <div
          class="drip-h4 font-weight-700 color-noble-blue-500 color-noble-blue-500"
        >
          {{ $t('legal.page1.title') }}
        </div>
        <div class="spacing-12"></div>
        <form class="legal-input-container" @submit.prevent="generateDocument">
          <!-- Date -->
          <div class="input-container">
            <div class="input-label drip-label color-noble-blue-500">
              {{ $t('legal.page1.date.label') }}
            </div>
            <div class="input-block">
              <div class="date-picker position-relative">
                <date-picker
                  ref="legalDate"
                  autocomplete="off"
                  name="legal_date"
                  v-model="document_date"
                  data-vv-as="Date"
                  :placeholder="$t('legal.page1.date.placeholder')"
                  :config="options"
                  v-validate="'required'"
                  class="date-input"
                >
                </date-picker>
                <span
                  class="action-input"
                  slot="caret"
                  @click="$refs.legalDate.$el.focus()"
                >
                  <b-icon-calendar-2-event-fill font-scale="0.9" />
                </span>
              </div>
              <span v-if="errors.has('legal_date')" class="error">
                {{ errors.first('legal_date') }}
              </span>
            </div>
          </div>
          <!-- City -->
          <div class="input-container">
            <div class="input-label drip-label color-noble-blue-500">
              {{ $t('legal.page1.city.label') }}
            </div>
            <div class="input-block">
              <input
                id="legal_city"
                name="legal_city"
                data-vv-as="City"
                type="text"
                v-model="city"
                :placeholder="$t('legal.page1.city.placeholder')"
                v-validate="'required|remove_specials|remove_number|max:45'"
              />
              <span v-if="errors.has('legal_city')" class="error">
                {{ errors.first('legal_city') }}
              </span>
            </div>
          </div>
          <action-button variant="solid" type="submit" extraClass="fullWidth">
            <b-icon-arrow-repeat
              v-if="loader"
              class="mr-2"
              animation="spin"
              scale="0.8"
            />
            {{ $t('legal.page1.button') }}
            <img
              v-if="!loader"
              src="@/assets/images/common/chevron-right-icon.svg"
              height="14"
              class="ml-2"
            />
          </action-button>
        </form>
      </div>
      <div v-else class="top-border">
        <div class="spacing-16"></div>
        <div class="drip-h4 font-weight-bold color-noble-blue-500 text-center">
          {{ $t('legal.page1.nonSignatoryMessage1') }}<br />
          {{ $t('legal.page1.nonSignatoryMessage2') }}
        </div>
      </div>
      <div class="spacing-32"></div>
      <div class="spacing-16"></div>
      <div class="legal-information-section">
        <div class="spacing-16"></div>
        <div class="d-flex align-items-center">
          <img src="@/assets/images/common/error-outline-blue.svg" width="20" />
          <div class="drip-h4 font-weight-700 color-noble-blue-500 ml-2">
            {{ $t('legal.page1.mandatoryInformation.title') }}
          </div>
        </div>
        <div class="spacing-4"></div>
        <div class="drip-body font-weight-400">
          {{ $t('legal.page1.mandatoryInformation.subtext') }}
        </div>
        <div class="spacing-16"></div>
        <div class="d-flex align-items-center">
          <b-icon-geo-alt class="mr-1 color-sky-blue-600" scale="1.2" />
          <div class="drip-body font-weight-700 color-noble-blue-500 ml-2">
            {{ $t('legal.page1.companyInformation.title') }}
          </div>
        </div>
        <div class="spacing-4"></div>
        <div class="drip-body font-weight-400">
          {{ $t('legal.page1.companyInformation.subtext') }}
        </div>
        <div class="spacing-16"></div>
        <div class="drip-body font-weight-700">
          {{
            $t('legal.page1.additionalContact', {
              name: salesManager.name
            })
          }}
          <span class="ml-2 color-noble-blue-500">|</span>
          <a
            :href="`mailto:${salesManager.email}`"
            class="color-wealthy-green-500 underline-on-hover"
          >
            {{ salesManager.email }}
          </a>
        </div>
        <div class="spacing-32"></div>
        <div class="spacing-32"></div>
        <div class="spacing-32"></div>
      </div>
    </div>
  </div>
  <div v-else>
    <loader :height="300" />
  </div>
</template>
<script>
import Loader from '@/components/common/Loader'
import DatePicker from 'vue-bootstrap-datetimepicker'
import ActionButton from '@/components/common/ActionButton'
import exportersService from '@/services/exporters'
import accountService from '@/services/accounts'
import { required } from 'vee-validate/dist/rules.esm'

export default {
  components: {
    ActionButton,
    DatePicker,
    Loader
  },
  data() {
    return {
      loader: false,
      document_date: '',
      city: '',
      salesManager: {},
      document_list: []
    }
  },
  created() {
    this.$validator.extend('remove_specials', {
      ...required,
      validate(value) {
        return /^[a-zA-Z0-9\s]*$/.test(value)
      }
    })
    this.$validator.extend('remove_number', {
      ...required,
      validate(value) {
        return !/[0-9]/.test(value)
      }
    })
  },
  async mounted() {
    this.$mpTrack('Legal page mounted')
    this.fetchLegalDetails()
    if (this.$route.query.agreement_date && this.$route.query.agreement_place) {
      this.document_date = this.$route.query.agreement_date
      this.city = this.$route.query.agreement_place
    }
    this.isUserSignedLegal()
  },
  methods: {
    async isUserSignedLegal() {
      const res = await accountService.fetchLoggedUser(
        this.$store.getters.token
      )
      const linkedUsers = res && res.data && res.data.linked_users
      const accountId =
        this.$store.state.account && this.$store.state.account.accountId
      const accountIndex =
        linkedUsers && linkedUsers.findIndex((x) => x.id === accountId)
      const isLegalSignedFlag =
        linkedUsers && linkedUsers[accountIndex || 0].legal_agreements_esigned
      if (isLegalSignedFlag) {
        // Refresh user stored in localStorage for browser refresh
        const user = JSON.parse(localStorage.getItem('user'))
        const accountIndex =
          user.linked_users &&
          user.linked_users.findIndex((x) => x.id === accountId)
        const index = accountIndex || 0
        user.linked_users[index].legal_agreements_esigned = true
        localStorage.setItem('user', JSON.stringify(user))

        // Refresh user status in vue store
        this.$store.dispatch('setEsignStatus', true)

        this.$router.push({
          name: 'dashboard',
          query: {
            legalEsign: 'true'
          }
        })
      }
    },
    isDocumentESigned() {
      if (
        this.$store.state.account &&
        this.$store.state.account.account &&
        this.$store.state.account.account.legal_agreements_esigned
      ) {
        this.$router.push('/')
      }
    },
    formatDate(str) {
      return this.$moment(str, 'DD/MM/YYYY').format('DD-MM-YYYY')
    },
    fetchLegalDetails() {
      exportersService
        .fetchLegalData(this.$store.state.account.accountId)
        .then((res) => {
          if (res) {
            const { name, email } = res.data.sales_manager
            this.salesManager = {
              name,
              email
            }
            this.document_list = res.data.documents
          }
        })
        .catch((e) => {
          console.log(e)
        })
    },
    generateDocument() {
      if (!this.loader) {
        return this.$validator
          .validate()
          .then((result) => {
            if (result) {
              this.loader = true
              const formData = {
                esign_fields: {
                  agreement_date: this.formatDate(this.document_date),
                  agreement_place: this.city
                }
              }
              exportersService
                .generateLegalEsignUrl(
                  this.$store.state.account.accountId,
                  formData
                )
                .then((res) => {
                  if (res) {
                    this.$mpTrack(
                      'Legal Signing - Generate legal agreement CTA clicked',
                      {
                        date: formData.esign_fields.agreement_date,
                        city: formData.esign_fields.agreement_place
                      }
                    )
                    this.$router.push({
                      name: 'legalEsign',
                      query: {
                        esign_url: res.data.esign_url,
                        file_url: res.data.file_url,
                        sales_manager_name: this.salesManager.name,
                        sales_manager_email: this.salesManager.email,
                        agreement_date: this.formatDate(this.document_date),
                        agreement_place: this.city
                      }
                    })
                  }
                  this.loader = false
                })
                .catch((e) => {
                  console.log(e)
                  this.loader = false
                  this.$notify({
                    title: 'Failed',
                    text: `Failed to generate document`,
                    type: 'error'
                  })
                })
            }
          })
          .catch((e) => {
            console.log(e)
            this.$notify({
              title: 'Failed',
              text: `Failed to generate document`,
              type: 'error'
            })
          })
      }
    }
  },
  computed: {
    isIndianExporter() {
      return (
        this.$store.state.account &&
        this.$store.state.account.account &&
        this.$store.state.account.country === 'India' &&
        this.$store.state.account.account.role === 'exporters'
      )
    },
    options() {
      return {
        format: 'DD/MM/YYYY',
        useCurrent: false,
        locale: this.$i18n.locale === 'es-MX' ? 'es' : 'en',
        minDate: this.$moment().format('YYYY-MM-DD')
      }
    }
  },
  watch: {
    document_date(newValue) {
      if (newValue) {
        this.$mpTrack('Legal Signing - Date selected', {
          date: newValue
        })
      }
    }
  }
}
</script>
<style lang="scss" scoped>
input {
  &:focus {
    box-shadow: 0 0 0 0.2rem rgba(39, 182, 124, 0.25);
  }
}
</style>
<style lang="scss" scoped>
.legal-container {
  text-align: center;
  .legal-inner-container {
    margin: 48px auto;
    width: 72%;
    @media screen and (max-width: $tablet) {
      margin: 12px auto;
      width: 100%;
    }
    .documents-section {
      width: 520px;
      margin: 0 auto;
      @media screen and (max-width: $tablet) {
        width: 100%;
        margin: auto;
      }
    }
    .legal-input-container {
      margin: auto;
      width: 400px;
      @media screen and (max-width: $tablet) {
        width: 100%;
      }
    }
    .top-border {
      border-top: 1px solid $wealthy-green-500;
    }
    .legal-information-section {
      text-align: left;
      color: $noble-blue-500;
      border-top: 1px solid $wealthy-green-500;
    }
  }
}
.input-container {
  width: 100%;
  margin-bottom: 16px;
  .input-label {
    font-size: 14px;
    font-weight: 400;
    line-height: 19px;
    letter-spacing: 0em;
    text-align: left;
    color: $noble-blue-500;
    margin-bottom: 8px;
  }
  .input-block {
    display: flex;
    flex-direction: column;
    text-align: left;
    .date-picker {
      .action-input {
        position: absolute;
        right: 0;
        height: 100%;
        width: 50px;
        top: 0;
        background: transparent;
        color: $wealthy-green-500;
        display: flex;
        align-items: center;
        justify-content: center;
        border-end-end-radius: 5px;
        border-start-end-radius: 5px;
        cursor: pointer;
      }
    }
    .drip-dropdown {
      height: 56px;
      width: 100%;
      max-width: 400px;
      ::v-deep .multiselect__select {
        height: 100%;
        &::before {
          z-index: 1;
        }
      }
      ::v-deep .multiselect__tags {
        outline: 0px;
        height: 56px;
        padding: 16px;
        padding-right: 24px;
        border-radius: 8px;
        border: 1px solid $noble-blue-500;
        color: $neutral-gray-500;
        font-weight: 400;
        margin-bottom: 4px;
        .multiselect__placeholder {
          color: $neutral-gray-500;
        }
        .multiselect__single {
          color: $noble-blue-500;
          font-weight: 700;
          vertical-align: bottom;
        }
      }
    }
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 30px white inset !important;
    }
    input {
      outline: 0px;
      width: 100%;
      max-width: 400px;
      height: 56px;
      padding: 16px;
      border-radius: 8px;
      border: 1px solid $noble-blue-500;
      background: $white;
      color: $noble-blue-500 !important;
      font-size: 16px;
      font-weight: 700;
      &::placeholder {
        font-weight: 400;
        background: $white;
        color: $neutral-gray-500;
      }
      &.width-800 {
        max-width: 800px;
      }
    }
  }
}
</style>
